import fscreen from 'fscreen';
import { DeviationPageStoreSlice } from './types';
import { reduxKey, defaultState } from './constants';
import { PapiDeviation } from '@wix/da-papi-types';
import { getCurrentModal } from '../../Modals/redux/selectors';
import { getIsMobile, getViewMode, getPublicSession } from '../../publicSession/selectors';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import { getCookieSize } from '@wix/da-hooks/pkg/useMeasureElement/redux/selectors';
import { DeviationViewMode } from '@wix/da-react-context/pkg/publicSession';
import ddt from '@wix/da-ddt';
import {
  getDeviationResourceType,
  getDeviationType,
  isLegacyStatus,
} from '../../DeviationViews/_helpers';
import { getBiEventComponentByType } from './helpers';
import { DeviationTypes } from '../../types/deviation';
import { getIsDuperbrowseActive } from '../../Duperbrowse/redux/selectors';
import { BlockReasons } from '../../Moderation/redux/types';
import { getHighestPriorityBlockReasonForDeviation } from '../../Moderation/redux/selectors';
import { getBasicTierSubscriptionInfo } from '../../utils/hooks/useSubscriptionTier';
import { normalizePremiumFolderData } from '../../utils/normalizePremiumFolderData';
import { createSelector } from 'reselect';
import isServerSideRendering from '../../utils/isSSR';
import { getEnv } from '@wix/da-react-app/pkg/client/Environment/redux/selectors';
import cssVars from '@wix/da-ds/src/styles/variables.scss';

const logger = ddt.createLogger('devpage');

const isSSR = typeof window === 'undefined';
const FORCED_THEATER_BREAKPOINT = parseInt(cssVars.forceTheaterBreakpoint, 10);

function getStoreSlice(state: DeviationPageStoreSlice) {
  return state[reduxKey] || defaultState;
}

export function getIsDuperbrowseAllowed(state: DeviationPageStoreSlice) {
  // if any modal is open, we don't want to move
  return !getCurrentModal(state as any);
}

function getScreenSize(state: DeviationPageStoreSlice) {
  return getStoreSlice(state).viewOptions.screenSize;
}

export function getIsTheaterForced(state: DeviationPageStoreSlice, deviation?: PapiDeviation) {
  if (getIsMobile(state)) {
    return true;
  }

  // commission don't have sidebar so they are effectively always theater
  if (deviation && getDeviationType(deviation) === DeviationTypes.COMMISSION) {
    return true;
  }

  const env = getEnv(state);
  const clientScreenSize = getScreenSize(state);
  const clientHintScreenSize = env.clientHints?.width;
  const cookieScreenSize = getCookieSize(state as any, MeasuredCookieType.SCREEN_WIDTH);

  // Get or guess the width
  // SSR - use the client hint if available, fallback to cookie or default
  // Client - use the measured value, fallback to hint/cookie/default
  let width: number;
  if (isServerSideRendering()) {
    width = clientHintScreenSize ?? cookieScreenSize?.[0] ?? 1280;
  } else {
    width = clientScreenSize?.width ?? clientHintScreenSize ?? cookieScreenSize?.[0] ?? 1280;
  }

  // force theater if art stage width + sidebar width < breakpoint
  return width <= FORCED_THEATER_BREAKPOINT;
}

export function getIsFullscreenAllowed(state) {
  if (!isSSR && !fscreen.fullscreenEnabled) {
    return false;
  }

  if (getIsMobile(state)) {
    return false;
  }

  return true;
}

function getIsFullscreen(state: DeviationPageStoreSlice) {
  const isBrowserFullscreen = !!fscreen.fullscreenElement;
  const isStateFullscreen = getViewMode(state) === DeviationViewMode.Fullscreen;

  // Allow element to declare itself as controlling fullscreen
  if (
    (isBrowserFullscreen && (fscreen.fullscreenElement as HTMLElement)?.dataset.canfs === 'yes') ||
    fscreen.fullscreenElement?.closest('[data-canfs]')
  ) {
    return false;
  }

  if (isStateFullscreen !== isBrowserFullscreen) {
    // weird shit happens sometimes
    logger.warn('Browser and store fullscreen state have diverged', {
      browser: isBrowserFullscreen,
      store: isStateFullscreen,
    });
  }

  // the browser is our real source of truth, store state is just to force react updates
  return isBrowserFullscreen;
}

function getIsTheater(state: DeviationPageStoreSlice, deviation?: PapiDeviation) {
  return getIsTheaterForced(state, deviation) || getViewMode(state) === DeviationViewMode.Theater;
}

export function getEffectiveViewMode(state: DeviationPageStoreSlice, deviation?: PapiDeviation) {
  if (getIsFullscreen(state)) {
    return DeviationViewMode.Fullscreen;
  }

  // special poll only support normal
  if (
    deviation &&
    (DeviationTypes.POLL === getDeviationType(deviation) || isLegacyStatus(deviation))
  ) {
    return DeviationViewMode.Normal;
  }

  if (getIsTheater(state)) {
    return DeviationViewMode.Theater;
  }

  return DeviationViewMode.Normal;
}

export function getBiContentViewStatus(state, deviation: PapiDeviation | undefined) {
  if (!deviation) {
    return undefined;
  }

  const biBlockReasons = {
    [BlockReasons.Antisocial]: 'antisocial_logged_out',
    [BlockReasons.MatureLoggedOut]: 'mature_logged_out',
    [BlockReasons.BlockedByArtist]: 'viewer_blocked',
    [BlockReasons.ArtistBlocked]: 'author_blocked',
    [BlockReasons.DeviationBlocked]: 'i_just_dont_want_to_see_this',
    [BlockReasons.StrictMatureAge]: 'strict_mature_underage',
    [BlockReasons.MatureAge]: 'mature_underage',
    [BlockReasons.MatureFilter]: 'mature_filter_on',
  };
  const blockReason = getHighestPriorityBlockReasonForDeviation(state, deviation);
  if (blockReason && biBlockReasons[blockReason] !== undefined) {
    return biBlockReasons[blockReason];
  }

  const { isPremiumLocked, isPaidFolder, isWatchToAccess } = normalizePremiumFolderData(
    deviation?.premiumFolderData
  );
  const deviationType = getDeviationType(deviation);
  if (isPremiumLocked && deviationType !== DeviationTypes.LITERATURE) {
    if (isPaidFolder) {
      return 'pg_locked';
    }
    if (isWatchToAccess) {
      return 'watchers_locked';
    }
  }

  const { needsSubscription, isSubscribedButBlocked } = getBasicTierSubscriptionInfo(deviation);
  if (needsSubscription || isSubscribedButBlocked) {
    return 'subscription_locked';
  }
}

export function getBiEventData(state, deviation: PapiDeviation) {
  return {
    view: !deviation.isPublished ? 'stash' : 'submission',
    publicSession: getPublicSession(state),
    eventComponent: getBiEventComponentByType(getDeviationType(deviation)),
    deviationId: deviation.deviationId,
    deviationResourceType: getDeviationResourceType(deviation),
    isDuperbrowse: getIsDuperbrowseActive(state),
    contentViewStatus: getBiContentViewStatus(state, deviation),
  };
}

export const getStashNext = createSelector(getStoreSlice, slice => {
  return slice.stash?.next;
});

export const getStashPrev = createSelector(getStoreSlice, slice => {
  return slice.stash?.prev;
});

export const getStashFolder = createSelector(getStoreSlice, slice => {
  return slice.stash?.folder;
});

export const getIsPostPublish = createSelector(getStoreSlice, slice => {
  return slice.postPublish ?? false;
});
